"use client"

import { useState, useRef, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { Check, ChevronsUpDown, Loader2, Settings2 } from 'lucide-react'
// framer-motion removed 2026-09-06 — see the note in top-header.tsx.
import { useStore } from '@/lib/store'
import { cn } from '@/lib/utils'
import { useT } from '@/lib/i18n'

/** A branch's monogram — its first letter on a quiet tile. Two branches are
 *  told apart by NAME, so the tile never needs a colour of its own. */
function Monogram({ name, active, size = 'md' }: { name: string; active?: boolean; size?: 'sm' | 'md' }) {
  return (
    <span className={cn(
      'flex items-center justify-center shrink-0 font-black uppercase',
      size === 'sm' ? 'size-6 rounded-md text-[12px]' : 'size-9 rounded-xl text-sm',
      active ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary',
    )}>
      {(name.trim().charAt(0) || '?')}
    </span>
  )
}

/**
 * Header branch switcher. Renders NOTHING for single-branch orgs — the
 * whole multi-branch layer stays invisible until a second branch exists.
 * The choice is per-device (localStorage) and re-hydrates the app through
 * the selected branch's stock lens.
 *
 * The shape is the 2026 workspace switcher (Linear, Vercel, Arc): the
 * trigger is the current branch as a monogram + name + ⇅; the menu is a
 * titled list of inset rows with the address under each name, the current
 * one tinted and checked, and a way to manage branches at the foot. It was a
 * teal-and-amber dropdown with a star — the owner: „ძალიან ჩამორჩება 2026
 * პრემიუმს" (2026-09-11).
 */
export function BranchSwitcher() {
  const t = useT()
  const router = useRouter()
  const branches = useStore((s) => s.branches)
  const activeBranchId = useStore((s) => s.activeBranchId)
  const setActiveBranch = useStore((s) => s.setActiveBranch)
  const isOwner = useStore((s) => s.userRole === 'owner')
  const [open, setOpen] = useState(false)
  const [switchingTo, setSwitchingTo] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (branches.length <= 1) return null

  const active = branches.find((b) => b.id === activeBranchId) ?? branches[0]
  const switching = switchingTo !== null

  const choose = async (id: string) => {
    if (id === activeBranchId || switching) { setOpen(false); return }
    setSwitchingTo(id)
    await setActiveBranch(id)
    setSwitchingTo(null)
    setOpen(false)
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={switching}
        aria-haspopup="menu"
        aria-expanded={open}
        title={t('branch.choose')}
        className={cn(
          'flex items-center gap-2 h-9 pl-1.5 pr-2 sm:pr-2.5 rounded-full md:rounded-xl border text-[13px] font-bold transition-colors duration-150 active:scale-[0.97] [-webkit-tap-highlight-color:transparent]',
          open
            ? 'bg-muted border-border text-foreground'
            : 'border-border bg-card dark:bg-white/5 hover:bg-muted text-foreground',
        )}
      >
        {switching
          ? <span className="size-6 flex items-center justify-center"><Loader2 className="size-3.5 animate-spin text-primary" /></span>
          : <Monogram name={active?.name ?? ''} size="sm" />}
        <span className="max-w-28 truncate hidden sm:block">{active?.name}</span>
        <ChevronsUpDown className="size-3.5 text-muted-foreground shrink-0" />
      </button>

      {/* 1.41 — anchored to the trigger's RIGHT edge on every width: the switcher
          sits in the header's right cluster, and a phone's left-anchored menu
          ran 288 px off the screen (the owner, 2026-10-14: „ნახევარი არ ჩანს"). */}
      {open && (
        <div
          role="menu"
          aria-label={t('branch.list')}
          className="animate-pop-down absolute top-11 right-0 z-50 w-72 max-w-[calc(100vw-2rem)] bg-popover text-popover-foreground rounded-2xl border border-border shadow-[0_20px_48px_-16px_oklch(0.25_0.03_255/0.28),0_4px_12px_-4px_oklch(0.25_0.03_255/0.08)] overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 pt-3 pb-2">
            <p className="text-[12px] font-bold uppercase tracking-[0.08em] text-muted-foreground">{t('branch.title')}</p>
            <span className="text-[12px] font-semibold text-muted-foreground tabular-nums">{branches.length}</span>
          </div>

          <div className="px-1.5 pb-1.5 flex flex-col gap-0.5 max-h-[60vh] overflow-y-auto">
            {branches.map((b) => {
              const isActive = b.id === activeBranchId
              const sub = [b.is_default && t('branch.main'), b.address].filter(Boolean).join(' · ')
              return (
                <button
                  key={b.id}
                  role="menuitemradio"
                  aria-checked={isActive}
                  onClick={() => choose(b.id)}
                  disabled={switching && switchingTo !== b.id}
                  className={cn(
                    'w-full flex items-center gap-3 px-2.5 py-2 rounded-xl text-left transition-colors duration-150 disabled:opacity-50',
                    isActive ? 'bg-primary/[0.08] dark:bg-primary/15' : 'hover:bg-muted',
                  )}
                >
                  <Monogram name={b.name} active={isActive} />
                  <span className="flex-1 min-w-0">
                    <span className={cn('block text-sm font-semibold truncate', isActive ? 'text-primary' : 'text-foreground')}>{b.name}</span>
                    {sub && <span className="block text-[12px] text-muted-foreground truncate">{sub}</span>}
                  </span>
                  {switchingTo === b.id
                    ? <Loader2 className="size-4 animate-spin text-primary shrink-0" />
                    : isActive && <Check className="size-4 text-primary shrink-0" strokeWidth={2.5} />}
                </button>
              )
            })}
          </div>

          {isOwner && (
            <div className="border-t border-border p-1.5">
              <button
                role="menuitem"
                onClick={() => { setOpen(false); router.push('/settings?s=branches') }}
                className="w-full flex items-center gap-3 px-2.5 py-2 rounded-xl text-left text-[13px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-150"
              >
                <span className="size-9 rounded-xl flex items-center justify-center shrink-0">
                  <Settings2 className="size-4" />
                </span>
                {t('branch.manage')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
